import { Trans, useLingui } from '@lingui/react/macro'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { getErrorMessage } from '@tshared/oapi/req/models'
import { App, Button, Form, Input, Result, Typography } from 'antd'
import { z } from 'zod'

import { useMobileResetPasswordMutation } from '@/apis/mobile-auth.api'
import { LockOutlined } from '@/icons'

import logo from '../-assets/logo.svg'
import { useFormValidation } from '../-hooks/useFormValidation'

const { Title } = Typography

export const Route = createFileRoute('/auth/reset-password/')({
  component: ResetPasswordPage,
  validateSearch: z.object({
    token: z.string().optional(),
  }),
})

type ResetPasswordFormValues = {
  newPassword: string
  confirmPassword: string
}

function ResetPasswordPage() {
  const { t } = useLingui()
  const { message } = App.useApp()
  const { token } = Route.useSearch()
  const { getPasswordRules } = useFormValidation()
  const resetMutation = useMobileResetPasswordMutation()
  const navigate = useNavigate()

  if (!token) {
    return <Result status="error" title={t`Invalid or expired reset link`} />
  }

  const onFinish = async (values: ResetPasswordFormValues) => {
    try {
      await resetMutation.mutateAsync({
        token,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      })
      void message.success(t`Password reset successfully`)
      void navigate({
        to: '/auth/success',
        search: {
          success_type: 'reset_password_success',
        },
      })
    } catch (error) {
      void message.error(
        getErrorMessage(error as Error, t`Password reset failed`) ?? t`Password reset failed`,
      )
    }
  }

  return (
    <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto', padding: '32px 0' }}>
      <div className="flex justify-center">
        <img src={logo} alt="" className="mb-8" />
      </div>
      <Title level={1} className="mb-1">
        <Trans>Reset Password</Trans>
      </Title>
      <p className="mb-6 text-sm text-gray-500">
        <Trans>Set a new password for your account</Trans>
      </p>

      <Form<ResetPasswordFormValues> onFinish={onFinish} layout="vertical" size="large">
        <Form.Item name="newPassword" rules={getPasswordRules(8)}>
          <Input.Password prefix={<LockOutlined />} placeholder={t`Enter new password`} />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: t`Please confirm new password!` },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) return Promise.resolve()
                return Promise.reject(new Error(t`Passwords do not match!`))
              },
            }),
          ]}
        >
          <Input.Password prefix={<LockOutlined />} placeholder={t`Enter new password again`} />
        </Form.Item>

        <Form.Item style={{ marginTop: 24 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={resetMutation.isPending}
            block
            size="large"
          >
            <Trans>Reset Password</Trans>
          </Button>
        </Form.Item>
      </Form>
    </div>
  )
}
